import Modal from "@via/components/modal";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { useDuplicatePipeline } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import {
  filterEmptyFolders,
  packDraftForSave,
  packScriptConfig,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { ModalSize } from "@via/utils/constants";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const DuplicatePipelineModal = ({ open, setOpen, draft, scripts }) => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState<any>(null);
  const { resetChangeCount } = useChangesDetect();

  const {
    mutateAsync: duplicatePipeline,
    isPending: isPipelineDuplicateLoading,
  } = useDuplicatePipeline();

  useEffect(() => {
    if (!draft || !scripts) return;

    const filteredEmptyFolders = filterEmptyFolders(scripts);
    const config = packScriptConfig(filteredEmptyFolders);
    const formatted = packDraftForSave(draft, config);
    const {
      name,
      writeGroupIds,
      groupId,
      pipelineUUId,
      pipelineRevUUId,
      permission,
      ...payload
    } = formatted;
    setPayload(payload);
  }, [draft, scripts]);

  // Awaiting the duplicate is what closes the dialog: resolving hands the close to
  // Modal, and a rejection leaves it open with the mutation's own error on screen.
  // The navigation moved here from a success effect for the same reason.
  //
  // resetChangeCount now runs only once the duplicate has actually succeeded. It
  // used to fire the moment the request was sent, so a failed duplicate cleared the
  // unsaved changes marker on an editor whose changes were still unsaved.
  const handleConfirmModal = async (): Promise<void> => {
    const duplicated = await duplicatePipeline(payload);
    resetChangeCount();
    navigate(`/pipelines/${duplicated.id}`);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Duplicate Pipeline"
      confirmText="Duplicate"
      size={ModalSize.Large}
      onCancel={() => setOpen(false)}
      isConfirmLoading={isPipelineDuplicateLoading}
      onConfirm={handleConfirmModal}>
      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
        <p>Are you sure you want to duplicate this pipeline?</p>
        <p className="mt-4">
          * It is not suggested to use this feature, unless you are planning to
          create custom pipeline.
        </p>
      </div>
    </Modal>
  );
};

export default DuplicatePipelineModal;
